import { useState } from "react";
import axios from "axios";
import { ArrowUpRight, Send } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactSection({ standalone = false }) {
  const [form, setForm] = useState({
    nombre: "",
    telefono: "",
    email: "",
    mensaje: "",
  });
  const [loading, setLoading] = useState(false);

  const setField = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.email || !form.mensaje) {
      toast.error("Por favor completá nombre, email y mensaje.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("¡Mensaje enviado! Te responderemos pronto.");
      setForm({ nombre: "", telefono: "", email: "", mensaje: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "No se pudo enviar el mensaje. Probá nuevamente."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contacto-form"
      data-testid="contact-section"
      className={`relative ${standalone ? "py-24" : "py-32 md:py-40"} border-t border-white/5`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#FFB800] mb-6 flex items-center gap-3">
              <span className="h-px w-10 bg-[#FFB800]" />
              07 — Contacto
            </p>
            <h2 className="font-display text-4xl md:text-6xl font-extrabold uppercase tracking-tight leading-[0.95]">
              ¿En qué<br />podemos<br />
              <span className="italic font-light text-white/70">ayudarte?</span>
            </h2>
            <p className="text-white/60 mt-8 max-w-md text-lg leading-relaxed">
              Escribinos y nos pondremos en contacto contigo. Estamos para
              ayudarte: socios, jugadores, sponsors o medios.
            </p>

            <div className="mt-12 space-y-5">
              <div className="border-l border-white/10 pl-4">
                <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">Ubicación</p>
                <p className="text-white">Barcelona — Cataluña, España</p>
              </div>
            </div>
          </div>

          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="lg:col-span-7 flex flex-col gap-2"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10">
              <Field
                label="Nombre"
                idx="01"
                value={form.nombre}
                onChange={setField("nombre")}
                placeholder="Tu nombre"
                testId="contact-input-nombre"
                required
              />
              <Field
                label="Teléfono"
                idx="02"
                value={form.telefono}
                onChange={setField("telefono")}
                placeholder="+34 600 000 000"
                testId="contact-input-telefono"
              />
              <Field
                label="Email"
                idx="03"
                type="email"
                value={form.email}
                onChange={setField("email")}
                placeholder="tu@email.com"
                testId="contact-input-email"
                required
              />
            </div>

            <div className="mt-2">
              <label className="flex justify-between items-baseline mb-2">
                <span className="text-[10px] uppercase tracking-[0.3em] text-white/45">
                  Mensaje
                </span>
                <span className="font-mono text-[10px] text-white/30">04</span>
              </label>
              <textarea
                rows={5}
                value={form.mensaje}
                onChange={setField("mensaje")}
                placeholder="Contanos brevemente cómo podemos ayudarte"
                data-testid="contact-input-mensaje"
                className="field-input resize-none"
                required
              />
            </div>

            <div className="mt-10 flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-6">
              <p className="text-xs text-white/40 max-w-sm">
                Al enviar este formulario aceptás nuestra política de privacidad.
                Te responderemos en menos de 48h.
              </p>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "Enviando..." : "Enviar mensaje"}
                {loading ? <Send size={14} /> : <ArrowUpRight size={14} />}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, idx, value, onChange, placeholder, type = "text", testId, required }) {
  return (
    <div className="mt-6">
      <label className="flex justify-between items-baseline mb-2">
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/45">{label}</span>
        <span className="font-mono text-[10px] text-white/30">{idx}</span>
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testId}
        className="field-input"
        required={required}
      />
    </div>
  );
}
